import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { trackSpiritualProfileEvent } from "../analytics/spiritualProfile";
import { ROUTE } from "../constants/routes";
import { useAddSpiritualEntityMutation } from "./useSpiritualProfile";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import type { FormChatState } from "../repositories/formChat.repository";
import { buildSpiritualProfileFormChatPayload } from "../utils/spiritualProfileFormChatPayloadBuilder";
import { toast } from "../utils/toast";

interface SubmitSpiritualProfileByChatInput {
  data: FormChatState;
}

export function useFormChatSubmitSpiritualProfile() {
  const navigate = useNavigate();
  const productBase = useProductAnalyticsBase();
  const { mutateAsync: addEntity } = useAddSpiritualEntityMutation();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};

  const submitSpiritualProfileByChat = useCallback(
    async ({ data }: SubmitSpiritualProfileByChatInput) => {
      const payload = buildSpiritualProfileFormChatPayload(data);
      if (!payload) {
        throw new Error("Incomplete spiritual profile chat state.");
      }

      trackSpiritualProfileEvent("spiritual_profile_form_chat_submitted", {
        terreiroId,
        mediumId,
        lineId: payload.lineId,
        entityId: payload.entityId,
        visibility: payload.visibility,
        developmentStage: payload.developmentStage,
        sourcePage: productBase.sourcePage,
      });

      try {
        await addEntity(payload);
        toast.success("Registro espiritual adicionado.");
        navigate(ROUTE.SPIRITUAL_PROFILE);
      } catch (error) {
        trackSpiritualProfileEvent("spiritual_profile_form_chat_submit_error", {
          terreiroId,
          mediumId,
          lineId: payload.lineId,
          entityId: payload.entityId,
          sourcePage: productBase.sourcePage,
        });
        throw error;
      }
    },
    [addEntity, mediumId, navigate, productBase.sourcePage, terreiroId],
  );

  return { submitSpiritualProfileByChat };
}
